import { useAnchorWallet } from '@solana/wallet-adapter-react';
import { createContext, useEffect, useState } from 'react';
import { UserBalanceModel, UserModel, UserProviderValue, UserReferralsModel } from '../models';
import { getUser, getUserBalance, getUserReferrals } from '../services';
import { useAuthProvider, useWebSocket } from '../hooks';
import { WebSocketEventType } from '../services/websocket.service';
import { userReferralsDefaultState } from '../constants';

export const UserContext = createContext({} as UserProviderValue);

export const UserProvider = ({ children }: any) => {
   const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);
   const wallet = useAnchorWallet();
   const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(undefined); // undefined = loading, false = not authenticated, true = authenticated
   const [user, setUser] = useState<UserModel | undefined>(undefined);
   const [userBalance, setUserBalance] = useState<UserBalanceModel | undefined>(undefined);
   const [userReferrals, setUserReferrals] = useState<UserReferralsModel>(userReferralsDefaultState);

   const { logOut } = useAuthProvider(isAuthenticated, setIsAuthenticated, fetchUser, setUser);

   async function fetchUser(): Promise<void> {
      getUser().then((response) => {
         if (response.error || !response.data) {
            setIsAuthenticated(false);
            return;
         }
         setIsAuthenticated(true);
         setUser(response.data);
      });
   }

   async function fetchUserBalance(): Promise<void> {
      if (!user) return;
      const balance = await getUserBalance();
      if (balance.error) return;
      setUserBalance(balance.data);
   }

   async function fetchUserReferrals(): Promise<void> {
      if (!user) return;
      const response = await getUserReferrals(userReferrals.pagination);
      if (response.error || !response.data) return;

      if (userReferrals.pagination.page > 1) {
         setUserReferrals({
            pagination: { ...userReferrals.pagination, total: response.data.counts.signups },
            userReferrals: [...(userReferrals.userReferrals ?? []), ...response.data.userReferrals],
            counts: response.data.counts,
            membershipRewards: response.data.membershipRewards,
         });
      } else {
         setUserReferrals({
            pagination: { ...userReferrals.pagination, total: response.data.counts.signups },
            userReferrals: response.data.userReferrals,
            counts: response.data.counts,
            membershipRewards: response.data.membershipRewards,
         });
      }
   }

   useEffect(() => {
      if (!user) return;
      fetchUserReferrals();
   }, [user, userReferrals.pagination.page]);

   useEffect(() => {
      if (!user || !isAuthenticated) {
         setUserBalance(undefined);
         setUserReferrals(userReferralsDefaultState);
         return;
      }
      fetchUserBalance();
      fetchUserReferrals();
   }, [user, isAuthenticated]);

   // Listen for WebSocket events to refresh balance (only when authenticated)
   useWebSocket(
      WebSocketEventType.REFRESH_BALANCE,
      () => {
         if (user && isAuthenticated) {
            fetchUserBalance();
         }
      },
      isAuthenticated === true,
      user?.id
   );

   return (
      <UserContext.Provider
         value={{
            isLoginModalOpen: isLoginModalOpen,
            setIsLoginModalOpen: setIsLoginModalOpen,
            user: user,
            setUser: setUser,
            balance: userBalance,
            setBalance: setUserBalance,
            userReferrals: userReferrals,
            setUserReferrals: setUserReferrals,
            wallet: wallet,
            isAuthenticated: isAuthenticated,
            setIsAuthenticated: setIsAuthenticated,
            fetchUser: fetchUser,
            logOut: logOut,
         }}>
         {children}
      </UserContext.Provider>
   );
};
